import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { addTag, removeTag } from '../api';
import { invalidateServerTruthLists } from '../inbox-debug';
import { useRefetchOnOpen } from '@/shared/hooks/useRefetchOnOpen';
import { useTagsList } from '../hooks';
import type { TicketTag } from '../types';

interface Props {
  ticketId: number;
  applied: TicketTag[];
  /** Só mostra as etiquetas, sem tirar nem pôr (atendimento de colega aberto
   *  só pra ver, via "Visualizar todos"). */
  readOnly?: boolean;
}

/**
 * Story 3.11 — chip de tags inline no header do ticket. Click no "+" abre popover de seleção.
 */
export function TagsPopover({ ticketId, applied, readOnly = false }: Props) {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const tagsQ = useTagsList();
  const qc = useQueryClient();

  // Abrir o popover busca as etiquetas — mesmo motivo do `/` no Composer:
  // elas não vencem por tempo, o gatilho é o atendente abrir a lista.
  useRefetchOnOpen(open, tagsQ.refetch);

  const addMut = useMutation({
    mutationFn: (tagId: number) => addTag(ticketId, tagId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ticket', ticketId] });
      invalidateServerTruthLists(qc, 'tagsPopover');
    },
  });
  const removeMut = useMutation({
    mutationFn: (tagId: number) => removeTag(ticketId, tagId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ticket', ticketId] });
      invalidateServerTruthLists(qc, 'tagsPopover');
    },
  });

  const appliedIds = new Set(applied.map((t) => t.id));
  const available = (tagsQ.data ?? []).filter(
    (t) => !appliedIds.has(t.id) && (t.scope === 'ticket' || t.scope === 'both'),
  );

  return (
    // `title=""` corta o aviso herdado do cabeçalho ("Informações do contato"):
    // sem ele, passar o mouse no "+ Tag", no X ou na lista mostrava o aviso do
    // cabeçalho, como se o clique fosse abrir as informações do contato.
    <div className="relative flex items-center gap-1.5" ref={popoverRef} title="">
      {applied.map((t) => (
        <span
          key={t.id}
          className="inline-flex items-center gap-1 rounded-sm px-2 py-[2px] font-semibold text-[10px] leading-[15px]"
          style={{ background: `${t.color}1f`, color: t.color }}
        >
          {t.name}
          {!readOnly && (
            <button
              type="button"
              onClick={() => removeMut.mutate(t.id)}
              aria-label={`Remover tag ${t.name}`}
              title="Remover tag"
              className="-mr-1 ml-0.5 rounded-pill p-0.5 hover:bg-black/5"
            >
              <X size={10} strokeWidth={2.5} />
            </button>
          )}
        </span>
      ))}
      {readOnly ? null : (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          title="Adicionar tag"
          className="inline-flex h-[19px] items-center gap-0.5 rounded-sm border border-n-300 border-dashed px-2 font-semibold text-[10px] text-n-600 hover:border-brand-500 hover:text-brand-600"
        >
          <Plus size={11} strokeWidth={2} />
          Tag
        </button>
      )}
      {open && (
        <div className="absolute top-[calc(100%+6px)] left-0 z-30 w-[240px] rounded-md border border-n-200 bg-n-0 p-2 shadow-md">
          {available.length === 0 ? (
            <div className="px-2 py-3 text-[12px] text-n-500">Nenhuma tag disponível.</div>
          ) : (
            <div className="flex flex-col gap-0.5">
              {available.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => {
                    addMut.mutate(t.id);
                    setOpen(false);
                  }}
                  className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-left text-[13px] text-n-700 hover:bg-n-50"
                >
                  <span
                    className="h-2.5 w-2.5 rounded-pill"
                    style={{ background: t.color }}
                    aria-hidden="true"
                  />
                  {t.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
